import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../contexts/ToastContext';
import { getAthleteStreak } from '../lib/database';
import { chooseHeroMetric } from '../lib/shareCard/heroMetric';
import { drawShareCard, CARD_W, CARD_H } from '../lib/shareCard/renderer';
import { loadPhotoFromFile } from '../lib/shareCard/photo';
import {
  createShareRecovery,
  isShareCancel,
  isShareAlreadyInProgress,
  forceRepaint,
  scheduleResumeCommit,
} from '../lib/shareCard/shareLifecycle';

/**
 * Sheet di condivisione della card post-workout.
 * - Anteprima Classic già pronta all'apertura (reveal animato in-app).
 * - Scatta foto / Scegli dalla galleria / Solo grafica.
 * - Con foto: drag + pinch-to-zoom + reset inquadratura, toggle dati alto/basso.
 * - Condividi: JPEG q0.92 → share sheet nativo, fallback "Salva immagine".
 * La foto resta SOLO sul dispositivo (nessun upload); l'export del canvas
 * non contiene EXIF/geotag per costruzione (i pixel vengono ri-encodati).
 */
function ShareCardSheet({ summary, xpResult, newBadges, workoutName, athleteId, onClose }) {
  const toast = useToast();
  const canvasRef = useRef(null);
  const overlayRef = useRef(null); // radice dello sheet, per il commit al resume
  const [streak, setStreak] = useState(null);   // null = in caricamento
  const [photo, setPhoto] = useState(null);     // canvas della foto (orientata, ridotta)
  const [placement, setPlacement] = useState('bottom');
  const [pos, setPos] = useState({ scale: 1, dx: 0, dy: 0 });
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);                // guardia sincrona anti doppio-tap
  const [canNativeShare, setCanNativeShare] = useState(true);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Streak fresca: query leggera on-demand (include il workout appena salvato)
  useEffect(() => {
    let alive = true;
    getAthleteStreak(athleteId)
      .then((s) => { if (alive) setStreak(s); })
      .catch(() => { if (alive) setStreak(0); }); // senza streak la card degrada bene
    return () => { alive = false; };
  }, [athleteId]);

  // capability share file (calcolata una volta con un file finto)
  useEffect(() => {
    try {
      const probe = new File([new Blob(['x'])], 'probe.jpg', { type: 'image/jpeg' });
      setCanNativeShare(!!(navigator.canShare && navigator.canShare({ files: [probe] })));
    } catch (e) {
      setCanNativeShare(false);
    }
  }, []);

  const heroMetric = streak === null
    ? null
    : chooseHeroMetric({ summary, newBadges, xpResult, streak });

  // Render (e re-render su ogni cambiamento di foto/posizione/toggle)
  const render = useCallback(() => {
    if (!canvasRef.current || !heroMetric) return;
    drawShareCard(canvasRef.current, {
      heroMetric,
      workoutName,
      date: new Date(),
      photo,
      photoPosition: pos,
      dataPlacement: placement,
    });
  }, [heroMetric, workoutName, photo, pos, placement]);

  useEffect(() => { render(); }, [render]);

  // render() cambia a ogni pan/pinch: il listener di visibilità qui sotto
  // usa un ref per non doversi ri-registrare in continuazione.
  const renderRef = useRef(render);
  useEffect(() => { renderRef.current = render; }, [render]);

  // ── Recupero al ritorno in foreground (G3).
  // Se al rientro la promise di navigator.share è ancora pending oltre la
  // grazia, la share è stata CONSEGNATA a un target esterno (Instagram,
  // WhatsApp, ...): su iOS il pannello nativo può risultare ancora
  // presentato e dal web NON è dismissabile. Fingere che sia tutto finito
  // (solo sbloccare il bottone) lascia lo sheet sotto un pannello fantasma
  // e la seconda share fallirebbe comunque: la scelta robusta è CHIUDERE lo
  // sheet e tornare al popup post-workout. Il token invalida gli esiti
  // tardivi della vecchia promise.
  const shareTokenRef = useRef(0);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const recoveryRef = useRef(null);
  if (!recoveryRef.current) {
    recoveryRef.current = createShareRecovery({
      onRecover: () => {
        shareTokenRef.current += 1; // la vecchia promise, se mai si risolve, viene ignorata
        busyRef.current = false;
        setBusy(false);
        onCloseRef.current(); // share consegnata fuori → si torna al popup
      },
    });
  }

  // ── Foto: input nascosti ──
  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // permette di riscegliere lo stesso file
    if (!file) return;
    try {
      const bitmap = await loadPhotoFromFile(file);
      setPhoto(bitmap);
      setPos({ scale: 1, dx: 0, dy: 0 });
    } catch (err) {
      toast.error('Non sono riuscito a leggere la foto. Prova con un\'altra immagine.');
    }
  };

  // ── Drag + pinch sull'anteprima (solo con foto) ──
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const cssScale = useRef(1); // px canvas per px CSS

  const onPointerDown = (e) => {
    if (!photo) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const rect = e.currentTarget.getBoundingClientRect();
    cssScale.current = CARD_W / rect.width;
    const pts = [...pointers.current.values()];
    gesture.current = {
      startPos: pos,
      startDist: pts.length === 2 ? Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) : null,
      startCenter: { x: pts.reduce((a, p) => a + p.x, 0) / pts.length, y: pts.reduce((a, p) => a + p.y, 0) / pts.length },
    };
  };

  const onPointerMove = (e) => {
    if (!photo || !gesture.current || !pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    const g = gesture.current;
    const center = { x: pts.reduce((a, p) => a + p.x, 0) / pts.length, y: pts.reduce((a, p) => a + p.y, 0) / pts.length };
    const k = cssScale.current;
    let next = {
      scale: g.startPos.scale,
      dx: g.startPos.dx + (center.x - g.startCenter.x) * k,
      dy: g.startPos.dy + (center.y - g.startCenter.y) * k,
    };
    if (pts.length === 2 && g.startDist) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      next.scale = Math.min(Math.max(g.startPos.scale * (dist / g.startDist), 1), 4);
    }
    setPos(next);
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) gesture.current = null;
    else onPointerDown(e); // ri-àncora la gesture con i pointer rimasti
  };

  // ── Ritorno in foreground (share verso app esterne, camera, background) ──
  const wasHiddenRef = useRef(false);
  useEffect(() => {
    // Sequenza di "risveglio", idempotente (può girare più volte al resume):
    // 1. reflow forzato (sblocca il layout se il compositor era congelato);
    // 2. ridisegno SINCRONO del canvas (iOS può averne svuotato il backing
    //    store in background);
    // 3. commit di compositing garantito: senza un frame NUOVO iOS continua
    //    a mostrare lo snapshot di sistema (fantasma del native share
    //    sheet) finché l'utente non tocca lo schermo — scheduleResumeCommit
    //    produce un diff reale e ridisegna di nuovo al primo frame eseguito;
    // 4. se lo share è rimasto "in volo", sblocca l'UI dopo la grazia.
    const wake = () => {
      wasHiddenRef.current = false;
      forceRepaint();
      renderRef.current();
      scheduleResumeCommit(overlayRef.current, () => renderRef.current());
      recoveryRef.current.handleVisible();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true;
        // gesture eventualmente interrotta a metà: mai pointer "fantasma"
        pointers.current.clear();
        gesture.current = null;
        return;
      }
      wake();
    };
    // focus: su iOS può arrivare prima (o al posto) del visibilitychange al
    // resume; sul desktop scatta a ogni cambio finestra → gate su un hidden
    // precedente per non rifare il lavoro a ogni focus.
    const onFocus = () => {
      if (wasHiddenRef.current) wake();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onVisibility); // difesa extra (resume)
    window.addEventListener('focus', onFocus);
    const recovery = recoveryRef.current;
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onVisibility);
      window.removeEventListener('focus', onFocus);
      recovery.dispose();
    };
  }, []);

  // ── Condivisione ──
  // JPEG q0.92: a parità visiva il file è 8-12× più piccolo del PNG (card
  // opaca, alpha inutile; Instagram ricomprime comunque) e l'encoding è più
  // rapido. Il canvas ri-encoda i pixel: mai EXIF/geotag della foto originale.
  const SHARE_FILE_NAME = 'uptra-workout.jpg';
  const SHARE_JPEG_QUALITY = 0.92;
  const makeShareFile = () => new Promise((resolve, reject) => {
    canvasRef.current.toBlob((blob) => {
      if (!blob) { reject(new Error('JPEG non generato')); return; }
      resolve(new File([blob], SHARE_FILE_NAME, { type: 'image/jpeg' }));
    }, 'image/jpeg', SHARE_JPEG_QUALITY);
  });

  const handleShare = async () => {
    if (busyRef.current || !heroMetric) return;
    busyRef.current = true;
    setBusy(true);
    // Token dello share corrente: se il watchdog fa recovery (promise persa
    // in background, iOS/PWA), gli esiti tardivi di QUESTO share sono ignorati.
    const token = ++shareTokenRef.current;
    try {
      render(); // il file esportato è ESATTAMENTE l'anteprima corrente
      const file = await makeShareFile();

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        const shareId = recoveryRef.current.start();
        try {
          await navigator.share({ files: [file] });
        } catch (err) {
          // Annullamento dello share sheet: non è un errore
          if (isShareCancel(err)) return;
          // WebKit ha ancora una share attiva ([[sharePromise]] non-null:
          // il pannello nativo precedente non si è davvero concluso).
          // MAI silenzioso: senza feedback il bottone sembra morto.
          if (isShareAlreadyInProgress(err)) {
            toast.info('La condivisione precedente è ancora aperta. Chiudila e riprova.');
            return;
          }
          // Esito tardivo dopo un recovery: lo share appartiene al passato
          if (shareTokenRef.current !== token) return;
          throw err;
        } finally {
          recoveryRef.current.settle(shareId);
        }
      } else {
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success('Immagine salvata ✓');
      }
    } catch (err) {
      if (shareTokenRef.current !== token) return; // errore tardivo post-recovery
      console.error('Share card failed:', err);
      toast.error('Non sono riuscito a generare la card. Riprova.');
      // l'utente resta nello sheet e può riprovare; il workout è già salvo
    } finally {
      // Dopo un recovery lo stato busy appartiene a uno share più recente:
      // solo lo share corrente può sbloccarlo.
      if (shareTokenRef.current === token) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  return ReactDOM.createPortal(
    // NIENTE backdrop-filter qui: su iOS un overlay full-screen con
    // backdrop-blur è un trigger noto di freeze del compositing al rientro
    // dal background (share sheet/app esterne). bg-black/80 rende uguale.
    <div ref={overlayRef} className="fixed inset-0 z-[1400] flex flex-col bg-black/80" role="dialog" aria-label="Condividi la tua card">
      {/* input nascosti: camera (senza obiettivo forzato) e galleria */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture className="hidden" onChange={handleFile} />
      <input ref={galleryInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

      {/* header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <h2 className="text-white font-bold text-base">La tua card</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Chiudi"
          className="w-9 h-9 rounded-full bg-white/15 text-white text-lg leading-none"
        >
          ✕
        </button>
      </div>

      {/* anteprima (reveal animato in-app) */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-6">
        <div
          className="share-card-reveal relative h-full max-h-full py-1"
          style={{ aspectRatio: `${CARD_W} / ${CARD_H}`, maxWidth: '78vw' }}
        >
          {heroMetric ? (
            <canvas
              ref={canvasRef}
              className="w-full h-full rounded-2xl shadow-2xl select-none"
              style={{ touchAction: 'none' }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
          ) : (
            <div className="w-full h-full rounded-2xl bg-white/10 animate-pulse" />
          )}
          {photo && (
            <p className="absolute -bottom-6 inset-x-0 text-center text-[11px] text-white/60">
              Trascina e pizzica per inquadrare
            </p>
          )}
        </div>
      </div>

      {/* controlli */}
      <div className="px-5 pt-7 pb-6 space-y-3" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
        <div className="flex gap-2">
          <button type="button" onClick={() => cameraInputRef.current?.click()}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border ${photo ? 'bg-white/10 text-white border-white/20' : 'bg-white/10 text-white border-white/20'}`}>
            📷 Scatta
          </button>
          <button type="button" onClick={() => galleryInputRef.current?.click()}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-white/10 text-white border border-white/20">
            🖼️ Galleria
          </button>
          <button type="button" onClick={() => { setPhoto(null); setPos({ scale: 1, dx: 0, dy: 0 }); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border ${!photo ? 'bg-white text-gray-900 border-white' : 'bg-white/10 text-white border-white/20'}`}>
            ✨ Solo grafica
          </button>
        </div>

        {photo && (
          <div className="flex gap-2">
            <div className="flex-1 flex rounded-xl overflow-hidden border border-white/20">
              <button type="button" onClick={() => setPlacement('top')}
                className={`flex-1 py-2.5 text-sm font-semibold ${placement === 'top' ? 'bg-white text-gray-900' : 'bg-white/10 text-white'}`}>
                Dati in alto
              </button>
              <button type="button" onClick={() => setPlacement('bottom')}
                className={`flex-1 py-2.5 text-sm font-semibold ${placement === 'bottom' ? 'bg-white text-gray-900' : 'bg-white/10 text-white'}`}>
                Dati in basso
              </button>
            </div>
            <button type="button" onClick={() => setPos({ scale: 1, dx: 0, dy: 0 })}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-white/10 text-white border border-white/20">
              ↺ Reimposta
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={handleShare}
          disabled={busy || !heroMetric}
          className="w-full py-4 rounded-2xl font-bold text-base bg-primary-600 text-white disabled:opacity-60"
        >
          {busy ? 'Preparo la card…' : canNativeShare ? 'Condividi' : 'Salva immagine'}
        </button>
      </div>

      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .share-card-reveal { animation: shareCardReveal .5s cubic-bezier(.2,.9,.3,1.2) both; }
          @keyframes shareCardReveal {
            from { opacity: 0; transform: translateY(24px) scale(.94); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
        }
      `}</style>
    </div>,
    document.body
  );
}

export default ShareCardSheet;
